import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip, Slider,
  Alert, useTheme, TextField,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import MapView from '../MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  readFileAsText, haversineDistance, parseCSVCoordinates, exportGeoJSON,
} from '../../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../../components/SEO';

const TRIP_COLORS = ['#6366f1', '#f43f5e', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#84cc16', '#f97316'];

function TripSegmentation() {
  const theme = useTheme();
  const [points, setPoints] = useState([]);
  const [timestamps, setTimestamps] = useState([]);
  const [error, setError] = useState('');
  const [timeGapMin, setTimeGapMin] = useState(5);
  const [distanceGapM, setDistanceGapM] = useState(500);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      try {
        const parsed = JSON.parse(text);
        const fc = parsed.type === 'FeatureCollection' ? parsed : { features: [parsed] };
        const pts = fc.features
          .filter(f => f.geometry?.type === 'Point')
          .map(f => ({
            coord: f.geometry.coordinates,
            ts: f.properties?.timestamp || f.properties?.time || f.properties?.datetime || null,
            props: f.properties || {},
          }));
        if (pts.length > 0) {
          setPoints(pts.map(p => p.coord));
          setTimestamps(pts.map(p => p.ts));
          setError('');
          return;
        }
        // Try LineString
        const lines = fc.features.filter(f => f.geometry?.type === 'LineString');
        if (lines.length > 0) {
          setPoints(lines[0].geometry.coordinates);
          setTimestamps([]);
          setError('');
          return;
        }
      } catch { /* Try CSV */ }

      const result = parseCSVCoordinates(text);
      if (result.coords.length > 0) {
        setPoints(result.coords);
        setTimestamps(result.timestamps);
        setError('');
        return;
      }
      throw new Error('No coordinates found');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const trips = useMemo(() => {
    if (points.length < 2) return [];
    const segments = [{ startIdx: 0, points: [points[0]] }];

    for (let i = 1; i < points.length; i++) {
      const dist = haversineDistance(points[i - 1], points[i]);
      let timeDiff = 0;
      if (timestamps[i] && timestamps[i - 1]) {
        timeDiff = (new Date(timestamps[i]) - new Date(timestamps[i - 1])) / 60000;
      }

      const isGap = dist > distanceGapM || (timestamps.length > 0 && timeDiff > timeGapMin);
      if (isGap) {
        segments.push({ startIdx: i, points: [points[i]] });
      } else {
        segments[segments.length - 1].points.push(points[i]);
      }
    }

    return segments.map((seg, i) => {
      let dist = 0;
      for (let j = 1; j < seg.points.length; j++) {
        dist += haversineDistance(seg.points[j - 1], seg.points[j]);
      }
      let duration = 0;
      if (timestamps[seg.startIdx] && timestamps[seg.startIdx + seg.points.length - 1]) {
        duration = (new Date(timestamps[seg.startIdx + seg.points.length - 1]) - new Date(timestamps[seg.startIdx])) / 1000;
      }
      return { id: i + 1, points: seg.points, distance: dist, duration, color: TRIP_COLORS[i % TRIP_COLORS.length] };
    });
  }, [points, timestamps, timeGapMin, distanceGapM]);

  const mapGeoJson = useMemo(() => {
    if (trips.length === 0) return null;
    return {
      type: 'FeatureCollection',
      features: trips.map(t => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: t.points },
        properties: { tripId: t.id, color: t.color },
      })),
    };
  }, [trips]);

  const styleFunction = useCallback((feature) => ({
    color: feature.properties.color,
    weight: 3.5,
    opacity: 0.85,
  }), []);

  const handleExportAll = () => {
    const fc = {
      type: 'FeatureCollection',
      features: trips.map(t => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: t.points },
        properties: { tripId: t.id, distance_m: t.distance, duration_s: t.duration },
      })),
    };
    exportGeoJSON(fc, 'geojson', 'segmented_trips');
  };

  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(0)} m`;
  const fmtDur = (s) => {
    if (s <= 0) return '—';
    const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Trip Segmentation | GPS Route Splitter | UmbrellaCo"
        description="Split continuous GPS traces into individual trips based on time and distance gaps. Free online tool for telematics log segmentation and mapping."
        keywords="trip segmentation, split gps route, route segmenter, gps distance gap, telematics trip log"
        url="/spatial-tools/trip-segmentation"
        jsonLd={createSoftwareAppSchema(
          'Trip Segmentation',
          'Split continuous GPS logs into logical trips based on time and distance thresholds.',
          'https://umbrellaco.in/spatial-tools/trip-segmentation',
          ['GPS Route Segmentation', 'Trip Logging', 'Distance Thresholding']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>GPS Trip Segmentation</Typography>
        <Typography variant="body2" color="text.secondary">
          Automatically divide a continuous bulk GPS trace into separate logical trips by detecting significant gaps in time or distance.
        </Typography>
      </Box>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={32} minSize={22}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ p: 2 }}>
              <Button component="label" variant="contained" fullWidth size="small" startIcon={<UploadFileIcon />}
                sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                {points.length > 0 ? `Loaded (${points.length} pts)` : 'Upload GPS Data'}
                <input type="file" hidden accept=".geojson,.json,.csv" onChange={handleUpload} />
              </Button>
              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                <ContentCutIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700}>Segmentation Rules</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Time Gap (minutes)</Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Slider value={timeGapMin} onChange={(e, v) => setTimeGapMin(v)} min={1} max={60} step={1} size="small" sx={{ flex: 1 }}
                  disabled={timestamps.length === 0} />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35 }}>{timeGapMin}m</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Distance Gap (meters)</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Slider value={distanceGapM} onChange={(e, v) => setDistanceGapM(v)} min={50} max={5000} step={50} size="small" sx={{ flex: 1 }} />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 45 }}>{distanceGapM}m</Typography>
              </Stack>
            </Box>
            <Divider />
            {trips.length > 0 && (
              <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {trips.length} Trips Detected
                </Typography>
                <Stack spacing={0.5}>
                  {trips.map(t => (
                    <Paper key={t.id} variant="outlined" sx={{ p: 1, borderLeft: `4px solid ${t.color}` }}>
                      <Typography variant="body2" fontWeight={700}>Trip {t.id}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.points.length} pts · {fmtDist(t.distance)} · {fmtDur(t.duration)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
            {trips.length > 0 && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                <Button variant="outlined" fullWidth size="small" startIcon={<DownloadIcon />} onClick={handleExportAll}>
                  Export All Trips
                </Button>
              </Box>
            )}
          </Box>
        </Panel>
        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />
        <Panel defaultSize={68} minSize={40}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {mapGeoJson ? (
              <MapView mapId="trip-seg-map" geoJsonData={mapGeoJson} styleFunction={styleFunction} />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)' }}>
                <Stack alignItems="center" spacing={2} sx={{ p: 4, textAlign: 'center' }}>
                  <ContentCutIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload GPS data to segment trips</Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default TripSegmentation;
